import React from 'react';
import { TextInput, Card, Badge } from '@parsina/framework';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { bffQuery, errorCode } from '../../lib/bffClient.js';
import { SEARCH_DRUGS_QUERY } from '../../graphql/documents.js';
import { DRUG_QUERY_MIN, DRUG_QUERY_DEBOUNCE_MS } from '../../screens/Visit/Visit.shared.js';

// The formulary type-ahead, and the deliberate hole in it.
//
// `searchDrugs` is FREE (core.md §8.3 — it is the AI service's `ListDrugs` and
// bills nothing), which is why it can run on a keystroke at all. It still asks
// only from the SECOND character and only 300 ms after the student stops typing:
// a one-letter query matches most of an Iranian formulary and returns a list
// nobody can read, and a query per keystroke turns one prescription into forty
// round trips.
//
// ── Free text is allowed, and it is marked ───────────────────────────────
//
// A student practising must be able to write a drug the formulary does not have.
// Typing one is legal and produces `drugId: null` — which is not a defect but a
// FACT with a consequence: the interaction checker works on formulary slugs, so
// a free-text row is skipped by it, and the row carries «خارج از فهرست دارویی»
// so nobody mistakes "no warning" for "no interaction" (core.md §7.5).

export default function DrugPicker({ value, onPick, onFreeText, disabled }) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState(value || '');
  const [results, setResults] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [searching, setSearching] = React.useState(false);

  React.useEffect(() => { setQuery(value || ''); }, [value]);

  React.useEffect(() => {
    const text = query.trim();
    if (text.length < DRUG_QUERY_MIN) { setResults([]); return undefined; }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(() => {
      bffQuery(SEARCH_DRUGS_QUERY, { query: text, limit: 12 })
        .then((data) => { if (alive) setResults(data?.searchDrugs || []); })
        .catch((err) => {
          // A formulary that did not answer must not stop a prescription being
          // written: free text is a first-class path, not a fallback.
          console.warn('[DrugPicker] search failed:', errorCode(err) || err?.message);
          if (alive) setResults([]);
        })
        .finally(() => { if (alive) setSearching(false); });
    }, DRUG_QUERY_DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);

  return (
    <div style={{ position: 'relative' }} data-testid="drug-picker">
      <TextInput
        label={t('visit.rx.drug')}
        required
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          onFreeText(e.target.value);
        }}
        onFocus={() => setOpen(true)}
        helperText={t('visit.rx.drugHint')}
        data-testid="drug-query"
      />
      {open && query.trim().length >= DRUG_QUERY_MIN && (results.length > 0 || searching) && (
        <Card
          style={{
            position: 'absolute', insetInlineStart: 0, insetInlineEnd: 0, top: '100%',
            zIndex: 30, maxHeight: 220, overflowY: 'auto', padding: 4,
          }}
          data-testid="drug-results"
        >
          {searching && !results.length && (
            <p style={{ margin: 0, padding: 8, fontSize: 12, color: CT.mute }}>
              {t('visit.rx.searching')}
            </p>
          )}
          {results.map((drug) => (
            <button
              key={drug.id}
              type="button"
              onClick={() => { onPick(drug); setQuery(drug.name); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'start', border: 0,
                background: 'none', font: 'inherit', cursor: 'pointer',
                padding: '7px 8px', borderRadius: 8, color: C.textPrimary, fontSize: 13,
              }}
              data-testid={`drug-option-${drug.id}`}
            >
              {drug.name}
              {drug.genericName && (
                <span style={{ fontSize: 11.5, color: CT.mute, marginInlineStart: 6 }}>
                  {drug.genericName}
                </span>
              )}
            </button>
          ))}
        </Card>
      )}
    </div>
  );
}

/** «خارج از فهرست دارویی» — a row the interaction check cannot see. */
export function FreeTextChip() {
  const { t } = useTranslation();
  return (
    <Badge variant="warning" data-testid="free-text-chip">{t('visit.rx.freeText')}</Badge>
  );
}
